import React, { useState, useReducer, useEffect } from "react";
import { useDispatch } from "react-redux";

// Components
import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import InputField from "../Components/Forms/InputField";
import ModalSearchChip from "./ModalSearchChip";
import SelectField from "../Components/Forms/SelectField";
import ImportModal from "../Components/Modals/Import-modal";

// Services
import { chipService } from "../../Services/chip.service";
import { supplierService } from "../../Services/suppliers.service";
import { networkService } from "../../Services/network.service";
import { storage } from "../../Services/storage.service";
import { toDateTimeISOString } from "../../Services/string.service";
import { importService } from "../../Services/import.service";
import { fileService } from "../../Services/file.service";

// Icons
import { AiOutlineSearch, AiFillSave } from "react-icons/ai";
import { FcSimCardChip } from "react-icons/fc";
import { CgImport } from "react-icons/cg";

// Constants
import { storageKeys } from "../../Constants/storage.key";

const _pageTitle = "Cadastro de Chips";
const _registerTitle = "Cadastrando novo Chip";
const _editTitle = "Editando Chip";

const formInitialValues = {
	id: 0,
	licenseeId: 0,
	lineNumber: "",
	iccid: "",
	mobileOperatorId: "",
	chipSupplierId: "",
	statusChip: "",
	privateAPNSince: "",
	isOwn: "true",
};

const mapSupplierToOption = (supplier) => ({
	key:
		supplier?.SupplierId ??
		supplier?.supplierId ??
		supplier?.id ??
		crypto.randomUUID(),
	id: supplier?.SupplierId ?? supplier?.supplierId ?? supplier?.id ?? "",
	name:
		supplier?.SupplierCode ??
		supplier?.supplierCode ??
		supplier?.SupplierName ??
		supplier?.supplierName ??
		"",
});

const mapNetworkToOption = (network) => ({
	key: network?.NetworkId ?? network?.networkId ?? network?.id ?? crypto.randomUUID(),
	id: network?.NetworkId ?? network?.networkId ?? network?.id ?? "",
	chipSupplierId:
		network?.ChipSupplierId ?? network?.chipSupplierId ?? "",
	chipSupplierName:
		network?.ChipSupplierName ?? network?.chipSupplierName ?? "",
	name:
		network?.NetworkName ??
		network?.networkName ??
		network?.name ??
		"",
});

const formReducer = (state, event) => {
	if (event.setter) {
		return event.chip;
	}
	if (event.reset) {
		return formInitialValues;
	}

	return {
		...state,
		[event.name]: event.value,
	};
};

const filterReducer = (state, event) => {
	if (event.reset) {
		return { lineNumber: "", iccid: "" };
	}
	return {
		...state,
		[event.name]: event.value,
	};
};

export default function Chips() {
	const [formData, setFormData] = useReducer(formReducer, {});
	const [isLoading, setIsLoading] = useState(false);
	const [toastParams, setToastParams] = useState("");
	const [showSearchModal, setShowSearchModal] = useState(false);
	const [actionTitle, setActionTitle] = useState("Dado do chip");
	const [showFields, setShowFields] = useState(false);
	const [, setShowDelete] = useState(false);
	const [filter, setFilter] = useReducer(filterReducer, {});
	const [, setSelectedChip] = useState({});
	const [listOperators, setListOperators] = useState([]);
	const [networks, setNetworks] = useState([]);
	const [listSuppliers, setListSuppliers] = useState([]);
	const [listStatus, setListStatus] = useState([]);
	const [showImportModal, setShowImportModal] = useState(false);

	const [settings] = useState(storage.get(storageKeys.settings));

	const dispatch = useDispatch();

	const showToast = (title, body, color) => {
		setToastParams(JSON.stringify({ show: true, title, body, color }));
	};

	const closeToast = () => {
		setToastParams(JSON.stringify({ show: false }));
	};

	useEffect(() => {
		dispatch({ type: "title", value: _pageTitle });
	}, [dispatch]);

	const onSearch = () => {
		closeToast();
		setShowSearchModal(true);
	};

	const handleSearchResult = (hasResult) => {
		if (!hasResult) {
			showToast("Atenção", "A busca não tem retorno.", "Warning");
		}
	};

	const handleSearchError = (errorInfo) => {
		showToast(
			"Erro",
			errorInfo?.message ||
				errorInfo?.friendlyMessage ||
				"Não foi possível realizar a busca no momento.",
			"Danger"
		);
	};

	const onNew = () => {
		setShowDelete(false);
		closeToast();
		setFormData({
			reset: true,
		});
		setShowFields(true);
		setActionTitle(_registerTitle);
	};

	const onCancel = () => {
		closeToast();
		setFormData({
			reset: true,
		});
		setShowFields(false);
		setShowDelete(false);
	};

	const onSave = async () => {
		if (!formData.lineNumber) {
			showToast("Atenção", "Forneça o Número da Linha!", "Warning");
			return;
		}
		if (!formData.mobileOperatorId) {
			showToast("Atenção", "Forneça a operadora!", "Warning");
			return;
		}
		if (!formData.id && !formData.chipSupplierId) {
			showToast("Atenção", "Forneça o Fornecedor de Chip!", "Warning");
			return;
		}
		if (!formData.iccid) {
			showToast("Atenção", "Forneça o ICCID!", "Warning");
			return;
		}
		if (!formData.statusChip) {
			showToast("Atenção", "Forneça o Status", "Warning");
			return;
		}

		// Define demais campos
		formData.licenseeId = settings.clientLicensedId;
		formData.privateAPNSince = toDateTimeISOString(new Date());

		try {
			setIsLoading(true);

			const isEditing = actionTitle === _editTitle || Number(formData.id) > 0;
			const payload = isEditing
				? {
					...formData,
					chipId: formData.id,
				}
				: formData;

			const result = isEditing
				? await chipService.update(payload)
				: await chipService.create(payload);

			// Backend recusou (ex.: linha/ICCID já cadastrado) — chip.service.js
			// não lança exceção nesse caso, devolve { error: true, message }.
			if (result?.error) {
				showToast(
					"Erro",
					result.message ||
						result.friendlyMessage ||
						(isEditing
							? "Não foi possível atualizar o chip. Verifique os dados e tente novamente."
							: "Não foi possível salvar o chip. Verifique os dados e tente novamente."),
					"Danger"
				);
				return;
			}

			const savedId =
				result?.chipId ??
				result?.ChipId ??
				result?.id ??
				formData.id ??
				null;

			setFormData({
				name: "id",
				value: savedId,
			});
			if (result !== null) {
				showToast(
					"Sucesso",
					isEditing ? "Chip atualizado com sucesso!" : "Chip salvo com sucesso!",
					"Success"
				);
			} else {
				showToast(
					"Erro",
					isEditing
						? "Não foi possível atualizar o chip. Verifique os dados e tente novamente."
						: "Não foi possível salvar o chip. Verifique os dados e tente novamente.",
					"Danger"
				);
			}
		} catch (error) {
			showToast("Erro", "Não foi possível salvar. Tente novamente mais tarde.", "Danger");
		} finally {
			setIsLoading(false);
		}
	};

	const handleFilterChange = (event) => {
		setFilter({
			name: event.target.name,
			value: event.target.value.trim(),
		});
	};

	const handleSelectChip = async (chip) => {
		const selectedChipId = chip?.id ?? chip?.chipId ?? chip?.ChipId ?? 0;

		setSelectedChip(chip);

		setFormData({
			chip: chip,
		});

		setShowFields(true);
		setShowSearchModal(false);
		setActionTitle(_editTitle);
		setShowDelete(true);

		// Buscar/usar listas dos selects

		const selectedStatusId =
			chip?.statusId ??
			chip?.StatusId ??
			chip?.statusChipId ??
			chip?.StatusChipId ??
			chip?.chipStatusId ??
			"";
		const normalizedSelectedStatusId =
			selectedStatusId === "" ? "" : Number(selectedStatusId);
		const selectedStatusName =
			chip?.statusName ??
			chip?.StatusName ??
			chip?.statusChip ??
			chip?.StatusChip ??
			chip?.chipStatusDescription ??
			chip?.ChipStatusDescription ??
			"";

		if (normalizedSelectedStatusId !== "" && Number.isFinite(normalizedSelectedStatusId)) {
			setListStatus((previousList) => {
				const alreadyExists = previousList.some(
					(item) => Number(item.id) === normalizedSelectedStatusId
				);

				if (alreadyExists) return previousList;

				return [
					{
						id: normalizedSelectedStatusId,
						name: selectedStatusName || `Status ${normalizedSelectedStatusId}`,
						key: crypto.randomUUID(),
					},
					...previousList,
				];
			});

			setFormData({
				name: "statusChip",
				value: normalizedSelectedStatusId,
			});
		} // eslint-disable-next-line

		const chipOperatorId =
			chip?.networkId ??
			chip?.NetworkId ??
			chip?.mobileOperatorId ??
			chip?.MobileOperatorId;
		const chipOperatorName =
			chip?.networkName ??
			chip?.NetworkName ??
			chip?.mobileOperatorName ??
			chip?.MobileOperatorName ??
			"";
		let supplierId = chip?.supplierId ?? chip?.SupplierId ?? "";
		let supplierName =
			chip?.supplierName ??
			chip?.SupplierName ??
			"";

		const networkFromState = networks.find(
			(network) => String(network.id) === String(chipOperatorId)
		);

		if (networkFromState && !supplierId) {
			supplierId =
				networkFromState?.chipSupplierId ??
				networkFromState?.ChipSupplierId ??
				"";
			supplierName =
				networkFromState?.chipSupplierName ??
				networkFromState?.ChipSupplierName ??
				supplierName;
		}

		const operatorFromList = listOperators.find(
			(operator) => String(operator.id) === String(chipOperatorId)
		);

		if (operatorFromList && !supplierId) {
			supplierId =
				operatorFromList?.chipSupplierId ??
				operatorFromList?.ChipSupplierId ??
				"";
			supplierName =
				operatorFromList?.chipSupplierName ??
				operatorFromList?.ChipSupplierName ??
				supplierName;
		}

		if (chipOperatorId && !supplierId) {
			const networkById = await networkService.list({
				NetworkId: chipOperatorId,
				Active: "S",
			});

			if (networkById && networkById.length > 0) {
				supplierName =
					networkById[0]?.chipSupplierName ??
					networkById[0]?.ChipSupplierName ??
					supplierName;
				supplierId =
					networkById[0]?.ChipSupplierId ?? networkById[0]?.chipSupplierId ?? "";
			}
		}

		if (!supplierId) {
			supplierId = chip?.chipSupplierId ?? chip?.ChipSupplierId ?? "";
			supplierName =
				chip?.chipSupplier ??
				chip?.ChipSupplier ??
				chip?.chipSupplierName ??
				chip?.ChipSupplierName ??
				supplierName;
		}

		if (supplierId) {
			if (!supplierName) {
				const supplierById = await supplierService.list({ SupplierId: supplierId });

				const supplierItem = Array.isArray(supplierById)
					? supplierById[0]
					: supplierById;

				supplierName =
					supplierItem?.supplierCode ??
					supplierItem?.SupplierCode ??
					supplierItem?.supplierName ??
					supplierItem?.SupplierName ??
					"";
			}

			setListSuppliers((previousList) => {
				const alreadyExists = previousList.some(
					(item) => String(item.id) === String(supplierId)
				);

				if (alreadyExists) return previousList;

				return [
					{
						id: supplierId,
						name: supplierName || `Fornecedor ${supplierId}`,
						key: crypto.randomUUID(),
					},
					...previousList,
				];
			});

			setFormData({
				name: "chipSupplierId",
				value: supplierId,
			});

			const supplierNetworks = await networkService.list({
				NetworkId: 0,
				ChipSupplierId: supplierId,
				Active: "S",
			});

			const operatorOptions = supplierNetworks
				? supplierNetworks.map((item) => mapNetworkToOption(item))
				: [];

			const hasSelectedOperator = operatorOptions.some(
				(item) => String(item.id) === String(chipOperatorId)
			);

			const finalOperatorOptions =
				chipOperatorId && !hasSelectedOperator
					? [
						{
							id: chipOperatorId,
							name: chipOperatorName || `Rede ${chipOperatorId}`,
							key: crypto.randomUUID(),
						},
						...operatorOptions,
					]
					: operatorOptions;

			setListOperators(finalOperatorOptions);

			const currentOperator = finalOperatorOptions.find(
				(item) => String(item.id) === String(chipOperatorId)
			);

			setFormData({
				name: "mobileOperatorId",
				value: currentOperator?.id ?? chipOperatorId ?? "",
			});
		} else if (chipOperatorId) {
			setListOperators((previousList) => {
				const alreadyExists = previousList.some(
					(item) => String(item.id) === String(chipOperatorId)
				);

				if (alreadyExists) return previousList;

				return [
					{
						id: chipOperatorId,
						name: chipOperatorName || `Rede ${chipOperatorId}`,
						key: crypto.randomUUID(),
					},
					...previousList,
				];
			});

			setFormData({
				name: "mobileOperatorId",
				value: chipOperatorId,
			});
		}

		setFormData({
			name: "id",
			value: selectedChipId,
		});
		setFormData({
			name: "lineNumber",
			value: chip.lineNumber ?? chip.numberLine,
		});
		setFormData({
			name: "iccid",
			value: chip.iccid,
		});
	};

	const handleFormChange = async (event) => {
		if (event.target.name === "chipSupplierId") {
			const supplierId = event.target.value;

			setFormData({
				name: "chipSupplierId",
				value: supplierId,
			});

			if (!supplierId) {
				setListOperators([]);
				setFormData({
					name: "mobileOperatorId",
					value: "",
				});
				return;
			}

			const supplierNetworks = await networkService.list({
				NetworkId: 0,
				ChipSupplierId: supplierId,
				Active: "S",
			});

			const operatorOptions = supplierNetworks
				? supplierNetworks.map((item) => mapNetworkToOption(item))
				: [];

			setListOperators(operatorOptions);

			setFormData({
				name: "mobileOperatorId",
				value: operatorOptions.length === 1 ? operatorOptions[0].id : "",
			});
			return;
		}

		if (event.target.name === "statusChip") {
			const statusId = event.target.value;
			const numericStatusId = statusId === "" ? "" : Number(statusId);

			if (numericStatusId === "" || Number.isNaN(numericStatusId)) {
				setFormData({
					name: "statusChip",
					value: "",
				});
				return;
			}

			const status = await chipService.getChipStatus(numericStatusId);
			const resolvedStatusId =
				status?.chipStatusId ?? status?.ChipStatusId ?? numericStatusId;
			setFormData({
				name: "statusChip",
				value: Number(resolvedStatusId),
			});
			return;
		}

		setFormData({
			name: event.target.name,
			value: event.target.value,
		});
	};

	const handleImport = async (file) => {
		let base64File = await fileService.toBase64(file);
		base64File = base64File.split("base64,")[1];

		var payload = {
			scheduleTypeId: "Chips",
			fileName: file.name,
			fileType: file.type,
			contentType: file.type,
			contentFile: base64File,
			usePeriod: false,
			dateTimeStart: toDateTimeISOString(new Date()),
			dateTimeEnd: toDateTimeISOString(new Date()),
			created: toDateTimeISOString(new Date()),
		};

		await importService.upload(payload);

		setShowImportModal(false);

		showToast(
			"Sucesso",
			"Importação do arquivo realizada. <a href='#/imports'>Clique aqui</a> para visualizar status das importações",
			"Success"
		);
	};

	const handleCloseImport = () => {
		setShowImportModal(false);
	};

	const onImport = () => {
		setShowImportModal(true);
	};

	useEffect(() => {
		(async () => {
			var response_suppliers = await supplierService.list();
			if (response_suppliers) {
				setListSuppliers(response_suppliers.map((item) => mapSupplierToOption(item)));
			}

			const response_networks = await networkService.list({
				NetworkId: 0,
				Active: "S",
			});

			if (Array.isArray(response_networks)) {
				const mappedNetworks = response_networks.map((item) => mapNetworkToOption(item));
				setNetworks(mappedNetworks);
			}

			const statusResponse = await chipService.getChipStatus(0);

			const statusList = Array.isArray(statusResponse)
				? statusResponse
				: Array.isArray(statusResponse?.items)
				? statusResponse.items
				: statusResponse
				? [statusResponse]
				: [];

			if (statusList.length > 0) {
				const mappedStatus = statusList.map((item) => ({
					id:
						Number(
							item?.statusId ??
							item?.StatusId ??
							item?.chipStatusId ??
							item?.ChipStatusId ??
							item?.statusChipId ??
							item?.id ??
							NaN
						),
					name:
						item?.statusName ??
						item?.StatusName ??
						item?.chipStatusDescription ??
						item?.ChipStatusDescription ??
						item?.statusChip ??
						item?.name ??
						"",
					key: crypto.randomUUID(),
				}));

				setListStatus(mappedStatus.filter((status) => Number.isFinite(status.id)));
			}
		})();
	}, []);

	return (
		<>
			<div className="container-xxl flex-grow-1 container-p-y">
				{isLoading ? <Spinner /> : ""}

				<ToastComponent params={toastParams} closeToast={closeToast} />

				<ModalSearchChip
					show={showSearchModal}
					handleClose={setShowSearchModal}
					filter={filter}
					handleSelect={handleSelectChip}
					onSearchResult={handleSearchResult}
					onSearchError={handleSearchError}
				/>
				<ImportModal
					title={"Chips"}
					callBackImport={handleImport}
					show={showImportModal}
					handleClose={handleCloseImport}
				/>

				{/* <DeleteModal
          itemName={isDeleting.itemName}
          itemRemoveId={isDeleting.itemRemoveId}
          callBackRemove={handleRemove}
          show={isDeleting.show}
          handleClose={handleCloseModal}
        /> */}

				<div className="row">
					<h5>Preencha os campos para buscar ou crie um novo</h5>
					<div className="row">
						<div className="mb-3 col-md-3">
							<div className="mb-3">
								<div className="input-group">
									<span className="input-group-text text-primary">
										Número da linha
									</span>
									<input
										tabIndex={1}
										type="text"
										className="form-control"
										aria-label="Número da linha"
										placeholder="Número da linha"
										onChange={handleFilterChange}
										aria-describedby="Número da linha"
										name="lineNumber"
									/>
								</div>
							</div>
						</div>
						<div className="mb-3 col-md-3">
							<div className="mb-3">
								<div className="input-group">
									<span className="input-group-text text-primary">ICCID</span>
									<input
										tabIndex={2}
										type="text"
										className="form-control"
										aria-label="iccid"
										aria-describedby="iccid"
										onChange={handleFilterChange}
										name="iccid"
										placeholder="iccid"
									/>
								</div>
							</div>
						</div>
						<div className="mb-3 col-md-4 col-lg-4">
							<div className="d-flex justify-content-around">
								<div>
									<button
										type="button"
										className="btn btn-primary"
										tabIndex={3}
										disabled={showFields || (!filter.lineNumber && !filter.iccid)}
										onClick={onSearch}
									>
										<AiOutlineSearch className="icon-in-button" />
										Buscar
									</button>
								</div>
								<div>
									<button
										type="button"
										className="btn btn-primary"
										disabled={showFields}
										onClick={onNew}
										tabIndex={4}
									>
										<FcSimCardChip className="icon-in-button" />
										&nbsp; Novo
									</button>
								</div>
								<div>
									<button
										type="button"
										className="btn btn-primary"
										onClick={onImport}
										tabIndex={5}
										style={{ display: "none" }}
									>
										<CgImport className="icon-in-button" />
										&nbsp; Importar
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					className="row"
					style={showFields ? { display: "" } : { display: "none" }}
				>
					<h4 className="fw-bold py-3 mb-4">
						<span className="text-muted fw-light">{actionTitle} </span>
					</h4>

					{formData.id ? (
						<div className="row mb-1">
							<label className="col-sm-4 col-form-label" htmlFor="licensePlate">
								Id: {formData.id || ""}
							</label>
						</div>
					) : (
						""
					)}

					<div className="col-4">
						<div className="row mb-3">
							<InputField
								label="Número da linha"
								name="lineNumber"
								type="text"
								tabIndex="5"
								grid_length="6"
								value={formData.lineNumber || ""}
								onChange={handleFormChange}
							/>
						</div>

						<div className="row mb-3">
							<SelectField
								name="chipSupplierId"
								label="Fornecedor de Chip"
								tabIndex="7"
								grid_length="8"
								value={formData.chipSupplierId || ""}
								itens={listSuppliers}
								onChange={handleFormChange}
							/>
						</div>

						<div className="row mb-3">
							<SelectField
								name="mobileOperatorId"
								label="Operadora"
								tabIndex="8"
								grid_length="8"
								value={formData.mobileOperatorId || ""}
								itens={listOperators}
								onChange={handleFormChange}
							/>
						</div>
					</div>
					<div className="col-4">
						<div className="row mb-3">
							<InputField
								label="ICCID"
								name="iccid"
								type="text"
								tabIndex="6"
								grid_length="6"
								value={formData.iccid || ""}
								onChange={handleFormChange}
							/>
						</div>

						<div className="row mb-3">
							<SelectField
								name="statusChip"
								label="Status"
								tabIndex="9"
								grid_length="8"
								itens={listStatus}
								value={formData.statusChip ?? ""}
								onChange={handleFormChange}
							/>
						</div>
					</div>

					<div className="mt-4">
						<button
							type="button"
							tabIndex={10}
							className="btn btn-primary me-2"
							onClick={async () => onSave()}
						>
							<AiFillSave className="icon-in-button" />
								&nbsp; Salvar
						</button>

						<button
							type="reset"
							className="btn btn-outline-secondary  me-2"
							onClick={onCancel}
							tabIndex={11}
						>
							Cancelar
						</button>

						{/* <button
              type='button'
              className='btn btn-danger'
              tabIndex={11}
              style={showDelete ? { display: "" } : { display: "none" }}>
              Remover
            </button> */}
					</div>
				</div>
			</div>
		</>
	);
}
